/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  AudioPresets,
  DefaultReconnectPolicy,
  type RoomOptions,
  ScreenSharePresets,
  type TrackPublishDefaults,
  type VideoPreset,
  VideoPresets,
} from "livekit-client";

/**
 * Video quality presets for the call
 */
export enum VideoQualityPreset {
  Regular = "regular",
  HD = "hd",
}

interface VideoQualityConfig {
  publishDefaults: TrackPublishDefaults;
  videoCaptureResolution: VideoPreset;
}

const basePublishOptions: Omit<
  TrackPublishDefaults,
  | "videoSimulcastLayers"
  | "videoCodec"
  | "videoEncoding"
  | "backupCodec"
  | "scalabilityMode"
> = {
  audioPreset: AudioPresets.music,
  dtx: true,
  // disable red because the livekit server strips out red packets for clients
  // that don't support it (firefox) but of course that doesn't work with e2ee.
  red: false,
  forceStereo: false,
  simulcast: true,
  screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
  stopMicTrackOnMute: false,
};

/**
 * Video quality configurations
 */
export const videoQualityConfigs: Record<VideoQualityPreset, VideoQualityConfig> = {
  [VideoQualityPreset.Regular]: {
    publishDefaults: {
      ...basePublishOptions,
      videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360] as VideoPreset[],
      videoCodec: "vp9",
      videoEncoding: VideoPresets.h720.encoding,
      backupCodec: { codec: "vp8", encoding: VideoPresets.h720.encoding },
    },
    videoCaptureResolution: VideoPresets.h720,
  },
  [VideoQualityPreset.HD]: {
    publishDefaults: {
      ...basePublishOptions,
      videoSimulcastLayers: [VideoPresets.h270, VideoPresets.h540] as VideoPreset[],
      videoCodec: "h265",
      videoEncoding: VideoPresets.h1080.encoding,
      backupCodec: { codec: "vp8", encoding: VideoPresets.h720.encoding },
    },
    videoCaptureResolution: VideoPresets.h720,
  },
};

/**
 * Get LiveKit options for a specific video quality preset
 */
export function getLiveKitOptions(preset: VideoQualityPreset): RoomOptions {
  const config = videoQualityConfigs[preset];
  return {
    // automatically manage subscribed video quality
    adaptiveStream: true,

    // optimize publishing bandwidth and CPU for published tracks
    dynacast: true,

    // capture settings
    videoCaptureDefaults: {
      resolution: config.videoCaptureResolution.resolution,
    },

    audioCaptureDefaults: {
      voiceIsolation: false,
      autoGainControl: false,
    },

    // publish settings
    publishDefaults: config.publishDefaults,

    // default LiveKit options that seem to be sane
    stopLocalTrackOnUnpublish: true,
    reconnectPolicy: new DefaultReconnectPolicy(),
    disconnectOnPageLeave: true,
    webAudioMix: false,
  };
}

// Default options using Regular quality for backward compatibility
export const defaultLiveKitOptions: RoomOptions = getLiveKitOptions(
  VideoQualityPreset.Regular,
);
